import React, { memo, useCallback, useMemo, ReactNode } from 'react';
import cx from 'clsx';

import { Dropdown } from '@/components';
import { IconRcFilter } from 'assets';

import style from './index.module.css';

type FilterProps = {
  className?: string;
  filters: Array<{ label: ReactNode; value: string | number | boolean }>;
  onFilterChange(idx: number, columnIdx?: number): void;
  currentFilterIdx: number;
  columnIdx?: number;
  showCurrent?: boolean;
  labelClassName?: string;
  children?: React.ReactChild;
};

export const DropdownFilter = memo(
  ({
    className,
    filters,
    currentFilterIdx,
    onFilterChange,
    columnIdx,
    showCurrent = false,
    labelClassName,
    children,
  }: FilterProps) => {
    const onFilterClick: React.MouseEventHandler<HTMLLIElement> = useCallback(
      e => {
        const {
          dataset: { idx },
        } = e.currentTarget;
        const sid = Number(idx);
        if (sid !== currentFilterIdx) {
          onFilterChange?.(sid, columnIdx);
        }
      },
      [onFilterChange, columnIdx, currentFilterIdx],
    );

    const _filters = useMemo(() => {
      return filters?.map(x => ({
        ...x,
        onClick: onFilterClick,
      }));
    }, [filters, onFilterClick]);

    const handleClick = useCallback(e => {
      e.stopPropagation();
    }, []);

    const isSelected = useMemo(() => !!currentFilterIdx, [currentFilterIdx]);

    return (
      <div onClick={handleClick} className={cx(style.filter, className)}>
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          overlayClassName={style.moreOverlayWrap}
          data={_filters}
          currentSelectedIdx={currentFilterIdx}>
          {showCurrent ? (
            <div className={labelClassName}>
              {filters[currentFilterIdx]?.label}
              {children}
            </div>
          ) : (
            <IconRcFilter
              onClick={handleClick}
              className={cx(style.filterIcon, isSelected && style.isSelected)}
            />
          )}
        </Dropdown>
      </div>
    );
  },
);

export default DropdownFilter;
